import React, { useCallback, useEffect, useState } from "react";
import {
	createAppBroadcast,
	createPushBroadcast,
	deleteAppBroadcast,
	previewAppBroadcastAudience,
	previewPushBroadcastReach,
	sendTestAppBroadcast,
	sendTestPushBroadcast,
} from "../../api/apiServiceFetch";
import {
	audienceOptions,
	formatCount,
	plural,
	sexOptions,
	testChannelOptions,
} from "./broadcastOptions";
import AppTestResult from "./AppTestResult";
import BroadcastImagePicker from "./BroadcastImagePicker";
import PushTestResult from "./PushTestResult";

// Совпадает с пределами на сервере (PushBroadcastService.BodyMaxLength /
// TitleMaxLength): длиннее сервер молча обрежет, а увидеть обрезку лучше здесь.
const PUSH_TITLE_MAX = 100;
const PUSH_BODY_MAX = 180;

// Предел сообщения в чате — тот же, что был у прежней формы рассылки.
const APP_MESSAGE_MAX = 4000;

/**
 * Что покажет уведомление, когда заголовок не задан.
 *
 * Имя приложения в заголовке не нужно: над уведомлением его и так пишет система
 * (Android/iOS) или браузер строкой «от Jerm». Заголовок «Jerm» давал три
 * одинаковых строки подряд. Пустой заголовок тоже не годится — браузер нарисовал
 * бы над текстом пустую жирную строку. Поэтому заголовком становится сам текст:
 * первая строка идёт в заголовок, остаток — телом. Ровно это делает сервер
 * (PushBroadcastService.ResolveTextAsync), здесь только показываем результат.
 */
const splitFirstLine = (text) => {
	const value = text || "";
	const breakAt = value.indexOf("\n");
	return breakAt < 0
		? [value.trim(), ""]
		: [value.slice(0, breakAt).trim(), value.slice(breakAt + 1).trim()];
};

const MODES = [
	{
		value: "both",
		title: "Сообщение в приложении + уведомление",
		hint: "Обычный случай: текст ложится в чат от профиля Jerm, а пуш сообщает, что сообщение пришло. Нажатие на уведомление открывает этот чат.",
	},
	{
		value: "app",
		title: "Только сообщение в приложении",
		hint: "Текст появится в чате от профиля Jerm, но человек узнает о нём, только когда сам зайдёт в приложение.",
	},
	{
		value: "push",
		title: "Только уведомление (пуш)",
		hint: "Уведомление на телефон или в браузер, без сообщения в чате. Нажатие просто открывает приложение.",
	},
];

/**
 * Составление рассылки: аудитория и текст задаются один раз, а способ доставки
 * выбирается отдельно. Главное на экране — разбор охвата: размер аудитории и
 * число тех, кто реально получит уведомление, различаются в разы, и без этого
 * числа по итогам кампании делаются неверные выводы.
 */
const BroadcastComposer = ({ onSent }) => {
	const [audienceType, setAudienceType] = useState(0);
	const [sex, setSex] = useState(0);
	const [mode, setMode] = useState("both");

	const [message, setMessage] = useState("");
	// Пусто — и остаётся пустым: заголовок с именем приложения дублировал бы
	// подпись, которую обе поверхности ставят сами.
	const [pushTitle, setPushTitle] = useState("");
	const [pushBody, setPushBody] = useState("");

	const [audienceCount, setAudienceCount] = useState(null);
	const [isCountLoading, setIsCountLoading] = useState(false);
	const [countError, setCountError] = useState(null);

	const [reach, setReach] = useState(null);
	const [isReachLoading, setIsReachLoading] = useState(false);
	const [reachError, setReachError] = useState(null);

	// Картинка одна на всю рассылку: одной загрузкой она уходит и сообщением в
	// чат, и с уведомлением. Ключ нужен серверу для уборки файла при отзыве,
	// support — ответ сервера о том, где картинка видна: врать про это дороже
	// всего.
	const [imageUrl, setImageUrl] = useState(null);
	const [imageKey, setImageKey] = useState(null);
	const [imageSupport, setImageSupport] = useState(null);
	// Смена key пересоздаёт поле выбора файла: иначе после отправки в нём
	// остаётся имя уже неиспользуемого файла.
	const [imagePickerKey, setImagePickerKey] = useState(0);

	const [testUserId, setTestUserId] = useState("");
	// «Обе поверхности сразу» — разумное значение по умолчанию для репетиции:
	// смысл проверки в том, чтобы увидеть каждую. Массовая рассылка, наоборот,
	// намеренно выбирает человеку ровно одну.
	const [testChannel, setTestChannel] = useState("both");
	const [isTesting, setIsTesting] = useState(false);
	const [testResult, setTestResult] = useState(null);
	const [testError, setTestError] = useState(null);
	const [hasTested, setHasTested] = useState(false);

	// Проба объявления: настоящая строка рассылки на одного человека. Её id нужен,
	// чтобы её же и отозвать — в истории рассылок она не появляется.
	const [isAppTesting, setIsAppTesting] = useState(false);
	const [appTestResult, setAppTestResult] = useState(null);
	const [appTestError, setAppTestError] = useState(null);
	const [isWithdrawingAppTest, setIsWithdrawingAppTest] = useState(false);
	const [appTestWithdrawn, setAppTestWithdrawn] = useState(false);

	const [confirm, setConfirm] = useState(null); // снимок чисел на момент подтверждения
	const [isPreparing, setIsPreparing] = useState(false);
	const [isSending, setIsSending] = useState(false);
	const [status, setStatus] = useState(null); // { ok, warn, text }

	const withApp = mode === "app" || mode === "both";
	const withPush = mode === "push" || mode === "both";
	const linkedToAppBroadcast = mode === "both";

	// Картинку принимает любой режим: сообщение в чате умеет фотографию так же,
	// как уведомление, и одна загрузка обслуживает оба. Поэтому при смене режима
	// картинка не сбрасывается — терять её было бы не за что.
	const effectiveImageUrl = imageUrl;

	// Рассылка из одной картинки — нормальное сообщение: подпись к фотографии
	// необязательна и в обычной переписке.
	const hasAppContent = Boolean(message.trim() || imageUrl);

	// Текст уведомления, который уйдёт на самом деле: пустое поле у совмещённой
	// рассылки сервер заполняет началом сообщения из чата.
	const effectivePushBody = (
		pushBody.trim() || (linkedToAppBroadcast ? message.trim() : "")
	).slice(0, PUSH_BODY_MAX);

	// Что человек увидит на экране. Заголовок задан — текст уходит телом целиком;
	// заголовок пуст — им становится первая строка текста, остаток остаётся телом.
	// Повторяет разбор на сервере: предпросмотр, который врёт, хуже, чем его
	// отсутствие.
	const [firstLine, restOfBody] = splitFirstLine(effectivePushBody);
	const hasOwnTitle = Boolean(pushTitle.trim());
	const previewTitle = (
		hasOwnTitle ? pushTitle.trim() : firstLine
	).slice(0, PUSH_TITLE_MAX);
	const previewBody = hasOwnTitle ? effectivePushBody : restOfBody;

	const loadAudienceCount = useCallback(async () => {
		setIsCountLoading(true);
		setCountError(null);
		try {
			const data = await previewAppBroadcastAudience(audienceType, sex);
			setAudienceCount(Number(data?.count ?? 0));
			return Number(data?.count ?? 0);
		} catch (error) {
			console.error("previewAppBroadcastAudience failed:", error);
			setAudienceCount(null);
			setCountError(
				`Не удалось посчитать аудиторию: ${error?.message || "запрос не удался"}`,
			);
			throw error;
		} finally {
			setIsCountLoading(false);
		}
	}, [audienceType, sex]);

	const loadReach = useCallback(async () => {
		setIsReachLoading(true);
		setReachError(null);
		try {
			const data = await previewPushBroadcastReach(
				audienceType,
				sex,
				linkedToAppBroadcast,
			);
			setReach(data);
			return data;
		} catch (error) {
			console.error("previewPushBroadcastReach failed:", error);
			setReach(null);
			setReachError(
				`Не удалось посчитать охват: ${error?.message || "запрос не удался"}`,
			);
			throw error;
		} finally {
			setIsReachLoading(false);
		}
	}, [audienceType, sex, linkedToAppBroadcast]);

	// Любая правка настроек делает прежние числа враньём, поэтому и подтверждение,
	// и результат прошлой отправки снимаются вместе с пересчётом.
	useEffect(() => {
		setConfirm(null);
		loadAudienceCount().catch(() => {});
	}, [loadAudienceCount]);

	useEffect(() => {
		setConfirm(null);
		if (!withPush) {
			setReach(null);
			setReachError(null);
			return;
		}
		loadReach().catch(() => {});
	}, [loadReach, withPush]);

	const clearImage = () => {
		setImageUrl(null);
		setImageKey(null);
		setImageSupport(null);
		setImagePickerKey((value) => value + 1);
	};

	const refreshCounts = () => {
		setConfirm(null);
		loadAudienceCount().catch(() => {});
		if (withPush) loadReach().catch(() => {});
	};

	const handleTest = async () => {
		const userId = Number(testUserId);
		if (!Number.isFinite(userId) || userId <= 0) {
			setTestError("Укажите id пользователя — число больше нуля.");
			setTestResult(null);
			return;
		}
		if (!effectivePushBody) {
			setTestError("Нечего отправлять: заполните текст уведомления.");
			setTestResult(null);
			return;
		}

		setIsTesting(true);
		setTestError(null);
		setTestResult(null);
		try {
			// appBroadcastId у репетиции всегда пустой: рассылки в приложении ещё
			// нет. Доставщик при этом тот же, что у массовой отправки, а канал
			// выбирает проверяющий: auto означает «как в массовой рассылке».
			const data = await sendTestPushBroadcast(
				userId,
				// Пусто и остаётся пустым: заголовок соберёт сервер из первой
				// строки текста, а имя приложения система подпишет сама.
				pushTitle.trim() || null,
				effectivePushBody,
				null,
				testChannel === "auto" ? null : testChannel,
				effectiveImageUrl,
			);
			setTestResult(data);
			setHasTested(true);
		} catch (error) {
			console.error("sendTestPushBroadcast failed:", error);
			setTestError(
				`Пробная отправка не удалась: ${error?.message || "запрос не удался"}`,
			);
		} finally {
			setIsTesting(false);
		}
	};

	/**
	 * Проба объявления: то же сообщение кладётся в чат ровно одному человеку.
	 * Строка создаётся настоящая — иначе проверять было бы нечего, — но помечена
	 * как пробная и в историю рассылок не попадает.
	 */
	const handleAppTest = async () => {
		const userId = Number(testUserId);
		if (!Number.isFinite(userId) || userId <= 0) {
			setAppTestError("Укажите id пользователя — число больше нуля.");
			setAppTestResult(null);
			return;
		}
		if (!hasAppContent) {
			setAppTestError(
				"Нечего отправлять: введите текст сообщения или приложите картинку.",
			);
			setAppTestResult(null);
			return;
		}

		setIsAppTesting(true);
		setAppTestError(null);
		setAppTestResult(null);
		setAppTestWithdrawn(false);
		try {
			const data = await sendTestAppBroadcast(
				userId,
				message.trim(),
				effectiveImageUrl,
			);
			setAppTestResult(data);
		} catch (error) {
			console.error("sendTestAppBroadcast failed:", error);
			setAppTestError(
				`Пробная отправка не удалась: ${error?.message || "запрос не удался"}`,
			);
		} finally {
			setIsAppTesting(false);
		}
	};

	// Проба отзывается тем же запросом, что и настоящая рассылка: это она и есть,
	// просто с одним получателем.
	const handleWithdrawAppTest = async () => {
		const id = appTestResult?.broadcastId;
		if (!id) return;

		setIsWithdrawingAppTest(true);
		setAppTestError(null);
		try {
			await deleteAppBroadcast(id);
			setAppTestWithdrawn(true);
		} catch (error) {
			console.error("deleteAppBroadcast failed:", error);
			setAppTestError(
				`Не удалось отозвать пробу №${id}: ${error?.message || "запрос не удался"}`,
			);
		} finally {
			setIsWithdrawingAppTest(false);
		}
	};

	// Подтверждение должно называть число, которое верно прямо сейчас, поэтому
	// перед показом рамки счётчики пересчитываются заново.
	const handleSendClick = async () => {
		setStatus(null);
		setConfirm(null);

		if (withApp && !hasAppContent) {
			setStatus({
				ok: false,
				text: "Введите текст сообщения для чата или приложите картинку.",
			});
			return;
		}
		if (withPush && !effectivePushBody) {
			setStatus({ ok: false, text: "Введите текст уведомления." });
			return;
		}

		setIsPreparing(true);
		try {
			const freshCount = await loadAudienceCount();
			const freshReach = withPush ? await loadReach() : null;
			setConfirm({
				audience: freshCount,
				willReceive: withPush ? Number(freshReach?.willReceive ?? 0) : 0,
			});
		} catch (error) {
			// Числа не получены — подтверждать нечего: подтверждение без числа
			// ничем не отличается от «вы уверены?».
			setStatus({
				ok: false,
				text: "Не удалось получить свежие цифры, отправка отменена. Повторите пересчёт.",
			});
		} finally {
			setIsPreparing(false);
		}
	};

	const handleSendConfirmed = async () => {
		setIsSending(true);
		setStatus(null);
		try {
			if (withApp) {
				const created = await createAppBroadcast(
					audienceType,
					sex,
					message.trim(),
					{
						announceByPush: withPush,
						pushTitle: pushTitle.trim() || null,
						pushBody: pushBody.trim() || null,
						// Одна картинка на объявление: сервер положит её и в чат, и
						// в уведомление, которое создаёт тем же запросом.
						imageUrl: effectiveImageUrl,
						imageKey: effectiveImageUrl ? imageKey : null,
					},
				);

				const total = Number(created?.totalUsers ?? 0);
				if (withPush && !created?.pushBroadcastId) {
					// Сообщение уже лежит в чатах, а уведомления не поставлены —
					// молчать об этом нельзя: админ решит, что пуши ушли.
					setStatus({
						ok: false,
						text: `Рассылка №${created?.id ?? "?"} отправлена в чат (${formatCount(total)}), но уведомления поставить не удалось. Отправьте их отдельно режимом «Только уведомление».`,
					});
				} else {
					setStatus({
						ok: true,
						text: withPush
							? `Рассылка №${created?.id ?? "?"} отправлена в чат (${formatCount(total)}), уведомления поставлены в очередь (задание №${created.pushBroadcastId}). Ход отправки виден в истории ниже.`
							: `Рассылка №${created?.id ?? "?"} отправлена в чат: ${formatCount(total)}. Уведомления не отправлялись.`,
					});
				}
			} else {
				const created = await createPushBroadcast(
					audienceType,
					sex,
					pushTitle.trim() || null,
					effectivePushBody,
					null,
					effectiveImageUrl,
				);
				setStatus({
					ok: true,
					text: `Задание №${created?.id ?? "?"} поставлено в очередь: уведомлений к отправке ${formatCount(Number(created?.totalTargets ?? 0))}.${
						effectiveImageUrl
							? " Уведомление уйдёт с картинкой — её увидят на Android и в Chrome/Edge, на iPhone придёт только текст."
							: ""
					} Ход отправки виден в истории ниже.`,
				});
			}

			setConfirm(null);
			setMessage("");
			setPushBody("");
			setTestResult(null);
			setHasTested(false);
			setAppTestResult(null);
			setAppTestError(null);
			setAppTestWithdrawn(false);
			clearImage();
			if (typeof onSent === "function") onSent();
			refreshCounts();
		} catch (error) {
			console.error("broadcast send failed:", error);
			setStatus({
				ok: false,
				text: `Ошибка отправки: ${error?.message || "запрос не удался"}`,
			});
		} finally {
			setIsSending(false);
		}
	};

	const willReceive = Number(reach?.willReceive ?? 0);

	return (
		<div className="broadcasts-page__section">
			<div className="broadcasts-page__section-head">
				<h2>Новая рассылка</h2>
				<button
					type="button"
					disabled={isCountLoading || isReachLoading}
					onClick={refreshCounts}
				>
					Пересчитать
				</button>
			</div>

			<div className="broadcasts-page__card">
				{/* ── Шаг 1: кому и что ───────────────────────────────────────── */}
				<div className="broadcasts-page__step">
					<p className="broadcasts-page__step-title">1. Кому и что отправляем</p>

					<div className="broadcasts-page__row">
						<label className="broadcasts-page__label">
							<span>Аудитория</span>
							<select
								value={audienceType}
								disabled={isSending}
								onChange={(e) => setAudienceType(Number(e.target.value))}
							>
								{audienceOptions.map((option) => (
									<option key={option.value} value={option.value}>
										{option.label}
									</option>
								))}
							</select>
						</label>

						<label className="broadcasts-page__label">
							<span>Пол</span>
							<select
								value={sex}
								disabled={isSending}
								onChange={(e) => setSex(Number(e.target.value))}
							>
								{sexOptions.map((option) => (
									<option key={option.value} value={option.value}>
										{option.label}
									</option>
								))}
							</select>
						</label>
					</div>

					<div
						className="broadcasts-page__modes"
						style={{ marginTop: 12, marginBottom: 12 }}
					>
						{MODES.map((item) => (
							<label
								key={item.value}
								className={`broadcasts-page__mode${
									mode === item.value ? " is-active" : ""
								}`}
							>
								<span className="broadcasts-page__mode-title">
									<input
										type="radio"
										name="broadcast-mode"
										value={item.value}
										checked={mode === item.value}
										disabled={isSending}
										onChange={() => {
											setMode(item.value);
											setStatus(null);
											setTestResult(null);
											// Картинка остаётся: её принимает любой режим, и
											// выбрасывать уже загруженный файл при переключении
											// было бы только неожиданностью.
										}}
									/>
									{item.title}
								</span>
								<p className="broadcasts-page__mode-hint">{item.hint}</p>
							</label>
						))}
					</div>

					{withApp && (
						<>
							<textarea
								className="broadcasts-page__textarea"
								value={message}
								rows={5}
								maxLength={APP_MESSAGE_MAX}
								placeholder="Текст сообщения — придёт в чат от профиля Jerm"
								disabled={isSending}
								onChange={(e) => {
									setMessage(e.target.value);
									setConfirm(null);
								}}
							/>
							<div className="broadcasts-page__row" style={{ marginTop: 6 }}>
								<span className="broadcasts-page__counter">
									{message.trim().length} / {APP_MESSAGE_MAX}
								</span>
							</div>
							{!message.trim() && imageUrl && (
								<p className="broadcasts-page__hint" style={{ marginTop: 0 }}>
									Текста нет — в чат уйдёт одна картинка, без подписи.
								</p>
							)}
						</>
					)}

					{withPush && (
						<>
							<div className="broadcasts-page__row" style={{ marginTop: 12 }}>
								<label className="broadcasts-page__label" style={{ flex: 1 }}>
									<span>Заголовок уведомления — необязательно</span>
									<input
										type="text"
										value={pushTitle}
										maxLength={PUSH_TITLE_MAX}
										placeholder="Пусто — заголовком станет первая строка текста"
										disabled={isSending}
										onChange={(e) => {
											setPushTitle(e.target.value);
											setConfirm(null);
										}}
									/>
								</label>
							</div>

							<p className="broadcasts-page__hint" style={{ marginTop: 6 }}>
								Имя приложения писать не нужно: телефон подписывает уведомление
								«Jerm» сам, а браузер добавляет строку «от Jerm». Заголовок «Jerm»
								даст три одинаковых строки подряд.
							</p>

							<div className="broadcasts-page__row">
								<label className="broadcasts-page__label" style={{ flex: 1 }}>
									<span>Текст уведомления</span>
									<input
										type="text"
										value={pushBody}
										maxLength={PUSH_BODY_MAX}
										placeholder={
											linkedToAppBroadcast
												? "Пусто — возьмём начало сообщения из чата"
												: "Короткая строка, которую человек увидит на экране"
										}
										disabled={isSending}
										onChange={(e) => {
											setPushBody(e.target.value);
											setConfirm(null);
										}}
									/>
								</label>
							</div>

							<div className="broadcasts-page__row" style={{ marginTop: 6 }}>
								<span className="broadcasts-page__counter">
									{pushBody.length} / {PUSH_BODY_MAX}
								</span>
							</div>

							{effectivePushBody && (
								<p
									className="broadcasts-page__hint"
									style={{ marginTop: 8, marginBottom: 0 }}
								>
									На экране будет: <b>{previewTitle}</b>
									{previewBody ? ` — ${previewBody}` : ""}
									{hasOwnTitle
										? ""
										: " (заголовка нет — им стала первая строка текста)"}
								</p>
							)}
						</>
					)}

					{/* Картинка общая для всех режимов: одна загрузка уходит и
					    сообщением в чат, и с уведомлением. */}
					<BroadcastImagePicker
						key={imagePickerKey}
						imageUrl={imageUrl}
						support={imageSupport}
						disabled={isSending}
						withApp={withApp}
						withPush={withPush}
						onChange={(picked) => {
							setImageUrl(picked?.imageUrl ?? null);
							setImageKey(picked?.imageKey ?? null);
							setImageSupport(picked?.support ?? null);
							setConfirm(null);
							setTestResult(null);
						}}
					/>
				</div>

				{/* ── Охват ───────────────────────────────────────────────────── */}
				<div className="broadcasts-page__step">
					<p className="broadcasts-page__step-title">Кто это получит</p>

					{!withPush ? (
						<>
							<p className="broadcasts-page__hint">
								Сообщение в приложении доходит до всей аудитории: оно просто
								лежит в чате и ждёт, когда человек зайдёт.
							</p>
							<div className="broadcasts-page__reach">
								<div className="broadcasts-page__reach-item broadcasts-page__reach-item--main">
									<div className="broadcasts-page__reach-label">
										Получат сообщение в чате
									</div>
									<div className="broadcasts-page__reach-value broadcasts-page__reach-value--main">
										{isCountLoading ? "..." : formatCount(audienceCount)}
									</div>
								</div>
							</div>
						</>
					) : (
						<>
							<p className="broadcasts-page__hint">
								Уведомление получат далеко не все: часть людей не давала
								разрешения на уведомления, у части подписка уже мертва. Разница
								между «аудиторией» и «получат уведомление» — это норма, а не
								сбой, но по итогам кампании считать надо именно правое число.
							</p>

							{isReachLoading && (
								<p className="broadcasts-page__hint">Считаем охват...</p>
							)}

							{reach && (
								<div className="broadcasts-page__reach">
									<div className="broadcasts-page__reach-item">
										<div className="broadcasts-page__reach-label">Аудитория</div>
										<div className="broadcasts-page__reach-value">
											{formatCount(reach.audience)}
										</div>
									</div>
									{/* Справка, а не ступень воронки: тумблер в настройках
									    объявление больше не гасит, поэтому это число ничего не
									    ограничивает — ни сверху, ни снизу. Оставлено видимым,
									    но приглушённым: читать по нему охват нельзя. */}
									<div className="broadcasts-page__reach-item broadcasts-page__reach-item--muted">
										<div className="broadcasts-page__reach-label">
											Оставили пуши включёнными
										</div>
										<div className="broadcasts-page__reach-value broadcasts-page__reach-value--muted">
											{formatCount(reach.pushEnabled)}
										</div>
										<div className="broadcasts-page__reach-note">
											справочно — на охват рассылки не влияет
										</div>
									</div>
									<div className="broadcasts-page__reach-item">
										<div className="broadcasts-page__reach-label">
											В приложении
										</div>
										<div className="broadcasts-page__reach-value">
											{formatCount(reach.mobile)}
										</div>
									</div>
									<div className="broadcasts-page__reach-item">
										<div className="broadcasts-page__reach-label">В браузере</div>
										<div className="broadcasts-page__reach-value">
											{formatCount(reach.web)}
										</div>
									</div>
									<div
										className={`broadcasts-page__reach-item broadcasts-page__reach-item--main${
											willReceive === 0
												? " broadcasts-page__reach-item--zero"
												: ""
										}`}
									>
										<div className="broadcasts-page__reach-label">
											Получат уведомление
										</div>
										<div
											className={`broadcasts-page__reach-value broadcasts-page__reach-value--main${
												willReceive === 0
													? " broadcasts-page__reach-value--zero"
													: ""
											}`}
										>
											{isReachLoading ? "..." : formatCount(willReceive)}
										</div>
									</div>
									{/* Подмножество правого числа, а не отдельная группа:
									    этих людей рассылка достаёт вопреки их настройке, и
									    видеть долю надо до отправки, а не после жалоб. */}
									<div className="broadcasts-page__reach-item broadcasts-page__reach-item--wide">
										<div className="broadcasts-page__reach-label">
											Из них отключали пуши в настройках — объявление получат
											всё равно
										</div>
										<div className="broadcasts-page__reach-value">
											{formatCount(reach.optedOutReached)}
										</div>
									</div>
								</div>
							)}

							{reach && (
								<p
									className="broadcasts-page__hint"
									style={{ marginTop: 10, marginBottom: 0 }}
								>
									И приложение, и браузер есть у {formatCount(reach.both)} —
									таким людям уйдёт одно уведомление, а не два.
									{linkedToAppBroadcast
										? " Мобильная часть сужена: нажатие на такое уведомление открывает чат от профиля Jerm, а старые сборки приложения этого не умеют."
										: ""}
								</p>
							)}

							{reachError && (
								<p className="broadcasts-page__status is-error">{reachError}</p>
							)}
						</>
					)}

					{countError && (
						<p className="broadcasts-page__status is-error">{countError}</p>
					)}
				</div>

				{/* ── Шаг 2: репетиция ────────────────────────────────────────── */}
				{/* Id получателя один на обе пробы: проверяют их обычно на себе, и
				    вводить одно и то же число дважды незачем. */}
				<div className="broadcasts-page__step">
					<p className="broadcasts-page__step-title">
						2. Пробная отправка — сделайте её первой
					</p>
					<p className="broadcasts-page__hint">
						Всё уйдёт <b>по-настоящему и одному настоящему человеку</b> — тем же
						путём, что и массовая рассылка. Укажите свой id или id тестового
						профиля. В историю рассылок пробы не попадают и её счётчиков не
						трогают.
					</p>

					<div className="broadcasts-page__row">
						<label className="broadcasts-page__label">
							<span>Id пользователя</span>
							<input
								type="number"
								value={testUserId}
								placeholder="например, 12345"
								disabled={isTesting || isAppTesting || isSending}
								onChange={(e) => {
									setTestUserId(e.target.value);
									setTestError(null);
									setAppTestError(null);
								}}
							/>
						</label>
					</div>

					{/* ── Проба объявления в чат ──────────────────────────────── */}
					{withApp && (
						<div className="broadcasts-page__step">
							<p className="broadcasts-page__step-title">
								Объявление в чат от профиля Jerm
							</p>
							<p className="broadcasts-page__hint">
								В чат этого человека ляжет <b>настоящее объявление</b> — оно
								рисуется тем же кодом, что и массовая рассылка, поэтому видно и
								как оно выглядит, и когда появляется. Уведомление при этом не
								отправляется: на него есть отдельная проба ниже. Убрать объявление
								можно кнопкой в ответе.
							</p>

							<div className="broadcasts-page__row">
								<button
									type="button"
									disabled={
										isAppTesting ||
										isSending ||
										!testUserId.trim() ||
										!hasAppContent
									}
									onClick={handleAppTest}
								>
									{isAppTesting
										? "Отправка..."
										: "Положить объявление в чат одному"}
								</button>
								<p className="broadcasts-page__hint broadcasts-page__hint--beside">
									Чат от профиля Jerm умеет не всякая сборка приложения — ответ
									скажет, увидит ли этот человек объявление вообще.
								</p>
							</div>

							{appTestError && (
								<p className="broadcasts-page__status is-error">{appTestError}</p>
							)}
							<AppTestResult
								result={appTestResult}
								onWithdraw={handleWithdrawAppTest}
								isWithdrawing={isWithdrawingAppTest}
								withdrawn={appTestWithdrawn}
							/>
						</div>
					)}

					{/* ── Проба уведомления ───────────────────────────────────── */}
					{withPush && (
						<div className="broadcasts-page__step">
							<p className="broadcasts-page__step-title">Уведомление (пуш)</p>
							<p className="broadcasts-page__hint">
								Уйдёт <b>настоящее уведомление</b> — тем же доставщиком, что и
								массовая рассылка. Сообщение в чате при этом не создаётся.
							</p>
							{linkedToAppBroadcast && (
								<p className="broadcasts-page__hint">
									Проба не привязана к сообщению в чате, поэтому уходит и на старые
									сборки приложения. Совмещённая рассылка их пропускает: нажатие на
									её уведомление должно открывать чат от профиля Jerm, а старая
									сборка так не умеет. То есть проба может дойти до того, кого сама
									рассылка не достанет.
								</p>
							)}

							<div className="broadcasts-page__row">
								<label className="broadcasts-page__label">
									<span>Куда отправить пробу</span>
									<select
										value={testChannel}
										disabled={isTesting || isSending}
										onChange={(e) => {
											setTestChannel(e.target.value);
											setTestResult(null);
											setTestError(null);
										}}
									>
										{testChannelOptions.map((option) => (
											<option key={option.value} value={option.value}>
												{option.label}
											</option>
										))}
									</select>
								</label>
								<button
									type="button"
									disabled={isTesting || isSending || !testUserId.trim()}
									onClick={handleTest}
								>
									{isTesting ? "Отправка..." : "Отправить пробное уведомление"}
								</button>
							</div>

							<p className="broadcasts-page__hint" style={{ marginTop: 8 }}>
								{testChannelOptions.find((o) => o.value === testChannel)?.hint}
							</p>
							{testChannel === "both" && (
								<p className="broadcasts-page__hint">
									Массовая рассылка отправляет каждому человеку ровно одно
									уведомление и сама выбирает поверхность — приложение важнее
									браузера. Поэтому «сразу обе» показывает больше, чем получит
									человек из аудитории: это проверка, а не предсказание.
								</p>
							)}
							{effectiveImageUrl && (
								<p className="broadcasts-page__hint">
									Проба уйдёт с картинкой. Если проверяете на iPhone — картинки там
									не будет ни при каких условиях, придёт только заголовок и текст.
								</p>
							)}

							{testError && (
								<p className="broadcasts-page__status is-error">{testError}</p>
							)}
							<PushTestResult result={testResult} />
						</div>
					)}
				</div>

				{/* ── Шаг 3: массовая отправка ────────────────────────────────── */}
				<div className="broadcasts-page__step">
					<p className="broadcasts-page__step-title">3. Массовая отправка</p>

					<div className="broadcasts-page__row">
						<button
							type="button"
							className="broadcasts-page__button--danger"
							disabled={
								isSending ||
								isPreparing ||
								Boolean(confirm) ||
								(withApp && !hasAppContent) ||
								(withPush && !effectivePushBody)
							}
							onClick={handleSendClick}
						>
							{isPreparing ? "Считаем получателей..." : "Отправить рассылку"}
						</button>
						<p className="broadcasts-page__hint broadcasts-page__hint--beside">
							Отменить отправленное уведомление нельзя — его увидят сразу.
						</p>
					</div>

					{confirm && (
						<div className="broadcasts-page__confirm">
							{withPush && (
								<p className="broadcasts-page__confirm-text">
									Уведомление получат прямо сейчас{" "}
									<strong>{formatCount(confirm.willReceive)}</strong>{" "}
									{plural(
										confirm.willReceive,
										"человек",
										"человека",
										"человек",
									)}
									. Отозвать уведомление нельзя: оно появится на экранах за
									несколько минут.
								</p>
							)}
							{withApp && (
								<p className="broadcasts-page__confirm-text">
									Сообщение появится в чате от профиля Jerm у{" "}
									<strong>{formatCount(confirm.audience)}</strong>{" "}
									{plural(confirm.audience, "человека", "человек", "человек")}.
									Его можно отозвать позже в истории — исчезнет у всех разом.
								</p>
							)}
							{effectiveImageUrl && withApp && (
								<p className="broadcasts-page__confirm-text">
									В чат уйдёт фотография
									{message.trim() ? " с текстом в подписи" : " без подписи"} —
									её увидят все получатели.
								</p>
							)}
							{effectiveImageUrl && withPush && (
								<p className="broadcasts-page__confirm-text">
									Уведомление уйдёт с картинкой. Её увидят на Android и в
									Chrome/Edge; на iPhone придёт только заголовок и текст.
								</p>
							)}
							{withPush && !hasTested && (
								<p className="broadcasts-page__confirm-text">
									Пробная отправка ещё не делалась. Проверьте текст на себе —
									это единственная возможность увидеть уведомление до того, как
									его увидят все.
								</p>
							)}
							<div className="broadcasts-page__row">
								<button
									type="button"
									className="broadcasts-page__button--danger"
									disabled={isSending}
									onClick={handleSendConfirmed}
								>
									{isSending
										? "Отправляем..."
										: withPush
											? `Да, отправить ${formatCount(confirm.willReceive)} ${plural(
													confirm.willReceive,
													"уведомление",
													"уведомления",
													"уведомлений",
												)}`
											: `Да, отправить сообщение ${formatCount(confirm.audience)} ${plural(
													confirm.audience,
													"человеку",
													"людям",
													"людям",
												)}`}
								</button>
								<button
									type="button"
									className="broadcasts-page__button--secondary"
									disabled={isSending}
									onClick={() => setConfirm(null)}
								>
									Отмена
								</button>
							</div>
						</div>
					)}

					{status && (
						<p
							className={`broadcasts-page__status ${
								status.ok ? "is-ok" : "is-error"
							}`}
						>
							{status.text}
						</p>
					)}
				</div>
			</div>
		</div>
	);
};

export default BroadcastComposer;
